import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  PayloadAction,
  EntityState,
} from "@reduxjs/toolkit";
import type { RootState } from "@/app/types";
import * as badgeApi from "@/api/badges";
import type {
  Badge,
  BadgeFilters,
  BadgeCreateRequest,
  BadgeUpdateRequest,
  HierarchyCreateRequest,
  PathAssignmentRequest,
  ItemAssignmentRequest,
} from "../types/badge.types";

/**
 * Badge Slice
 * Manages CRUD operations for badges and hierarchy
 * Part of Feature: Badge and Points Management System
 */

// Entity adapter for normalized state management with UUID
const badgesAdapter = createEntityAdapter<Badge, string>({
  selectId: (badge: Badge) => badge.id,
  sortComparer: (a, b) => {
    // Sort by updatedAt descending (most recently updated first)
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  },
});

// State interface
interface BadgeState {
  badges: EntityState<Badge, string>;
  selectedBadgeId: string | null;
  loading: boolean;
  error: string | null;
  createLoading: boolean;
  createError: string | null;
  updateLoading: boolean;
  updateError: string | null;
  deleteLoading: boolean;
  deleteError: string | null;
  hierarchyLoading: boolean;
  hierarchyError: string | null;
  filters: {
    searchQuery: string;
    type?: "parent" | "sub";
    active?: boolean;
    parentBadgeId?: string;
  };
  total: number;
}

// Initial state
const initialState: BadgeState = {
  badges: badgesAdapter.getInitialState(),
  selectedBadgeId: null,
  loading: false,
  error: null,
  createLoading: false,
  createError: null,
  updateLoading: false,
  updateError: null,
  deleteLoading: false,
  deleteError: null,
  hierarchyLoading: false,
  hierarchyError: null,
  filters: {
    searchQuery: "",
    type: undefined,
    active: true,
    parentBadgeId: undefined,
  },
  total: 0,
};

// Async Thunks

/**
 * Fetch badges with filters
 */
export const fetchBadges = createAsyncThunk(
  "badges/fetchBadges",
  async (filters: BadgeFilters | undefined, { rejectWithValue }) => {
    try {
      const badges = await badgeApi.fetchBadges(filters);
      return badges;
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Failed to fetch badges",
      );
    }
  },
);

/**
 * Fetch a single badge by ID
 */
export const fetchBadgeById = createAsyncThunk(
  "badges/fetchBadgeById",
  async (badgeId: string, { rejectWithValue }) => {
    try {
      const badge = await badgeApi.fetchBadgeById(badgeId);
      return badge;
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Failed to fetch badge",
      );
    }
  },
);

/**
 * Create a new badge
 */
export const createBadge = createAsyncThunk(
  "badges/createBadge",
  async (payload: BadgeCreateRequest, { rejectWithValue }) => {
    try {
      const badge = await badgeApi.createBadge(payload);
      return badge;
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Failed to create badge",
      );
    }
  },
);

/**
 * Update an existing badge
 */
export const updateBadge = createAsyncThunk(
  "badges/updateBadge",
  async (
    { id, payload }: { id: string; payload: BadgeUpdateRequest },
    { rejectWithValue },
  ) => {
    try {
      const badge = await badgeApi.updateBadge(id, payload);
      return badge;
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Failed to update badge",
      );
    }
  },
);

/**
 * Delete a badge
 */
export const deleteBadge = createAsyncThunk(
  "badges/deleteBadge",
  async (badgeId: string, { rejectWithValue }) => {
    try {
      await badgeApi.deleteBadge(badgeId);
      return badgeId;
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Failed to delete badge",
      );
    }
  },
);

/**
 * Activate a badge
 */
export const activateBadge = createAsyncThunk(
  "badges/activateBadge",
  async (badgeId: string, { rejectWithValue }) => {
    try {
      const badge = await badgeApi.activateBadge(badgeId);
      return badge;
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Failed to activate badge",
      );
    }
  },
);

/**
 * Deactivate a badge
 */
export const deactivateBadge = createAsyncThunk(
  "badges/deactivateBadge",
  async (badgeId: string, { rejectWithValue }) => {
    try {
      const badge = await badgeApi.deactivateBadge(badgeId);
      return badge;
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Failed to deactivate badge",
      );
    }
  },
);

/**
 * Assign badge to learning path
 */
export const assignBadgeToPath = createAsyncThunk(
  "badges/assignBadgeToPath",
  async (payload: PathAssignmentRequest, { rejectWithValue }) => {
    try {
      const result = await badgeApi.assignBadgeToPath(payload);
      return result;
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Failed to assign badge to path",
      );
    }
  },
);

/**
 * Assign badge to path item
 */
export const assignBadgeToItem = createAsyncThunk(
  "badges/assignBadgeToItem",
  async (payload: ItemAssignmentRequest, { rejectWithValue }) => {
    try {
      const result = await badgeApi.assignBadgeToItem(payload);
      return result;
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Failed to assign badge to item",
      );
    }
  },
);

/**
 * Create badge hierarchy relationship
 */
export const createHierarchy = createAsyncThunk(
  "badges/createHierarchy",
  async (payload: HierarchyCreateRequest, { rejectWithValue }) => {
    try {
      const result = await badgeApi.createHierarchy(payload);
      return result;
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: { error?: string } } }).response?.data
          ?.error || "Failed to create badge hierarchy",
      );
    }
  },
);

// Slice
const badgeSlice = createSlice({
  name: "badges",
  initialState,
  reducers: {
    setSelectedBadgeId: (state, action: PayloadAction<string | null>) => {
      state.selectedBadgeId = action.payload;
    },
    clearSelectedBadge: (state) => {
      state.selectedBadgeId = null;
    },
    setFilters: (
      state,
      action: PayloadAction<Partial<BadgeState["filters"]>>,
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearErrors: (state) => {
      state.error = null;
      state.createError = null;
      state.updateError = null;
      state.deleteError = null;
      state.hierarchyError = null;
    },
    clearBadges: (state) => {
      state.badges = badgesAdapter.getInitialState();
      state.total = 0;
    },
  },
  extraReducers: (builder) => {
    // Fetch badges
    builder
      .addCase(fetchBadges.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBadges.fulfilled, (state, action) => {
        state.loading = false;
        badgesAdapter.setAll(state.badges, action.payload);
        state.total = action.payload.length;
      })
      .addCase(fetchBadges.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch badge by ID
    builder
      .addCase(fetchBadgeById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBadgeById.fulfilled, (state, action) => {
        state.loading = false;
        badgesAdapter.upsertOne(state.badges, action.payload);
      })
      .addCase(fetchBadgeById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create badge
    builder
      .addCase(createBadge.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createBadge.fulfilled, (state, action) => {
        state.createLoading = false;
        badgesAdapter.addOne(state.badges, action.payload);
        state.total += 1;
        state.selectedBadgeId = action.payload.id;
      })
      .addCase(createBadge.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload as string;
      });

    // Update badge
    builder
      .addCase(updateBadge.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updateBadge.fulfilled, (state, action) => {
        state.updateLoading = false;
        badgesAdapter.upsertOne(state.badges, action.payload);
      })
      .addCase(updateBadge.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload as string;
      });

    // Delete badge
    builder
      .addCase(deleteBadge.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteBadge.fulfilled, (state, action) => {
        state.deleteLoading = false;
        badgesAdapter.removeOne(state.badges, action.payload);
        state.total -= 1;
        if (state.selectedBadgeId === action.payload) {
          state.selectedBadgeId = null;
        }
      })
      .addCase(deleteBadge.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload as string;
      });

    // Activate badge
    builder
      .addCase(activateBadge.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(activateBadge.fulfilled, (state, action) => {
        state.updateLoading = false;
        badgesAdapter.upsertOne(state.badges, action.payload);
      })
      .addCase(activateBadge.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload as string;
      });

    // Deactivate badge
    builder
      .addCase(deactivateBadge.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(deactivateBadge.fulfilled, (state, action) => {
        state.updateLoading = false;
        badgesAdapter.upsertOne(state.badges, action.payload);
      })
      .addCase(deactivateBadge.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload as string;
      });

    // Assign badge to path
    builder
      .addCase(assignBadgeToPath.pending, (state) => {
        state.hierarchyLoading = true;
        state.hierarchyError = null;
      })
      .addCase(assignBadgeToPath.fulfilled, (state) => {
        state.hierarchyLoading = false;
      })
      .addCase(assignBadgeToPath.rejected, (state, action) => {
        state.hierarchyLoading = false;
        state.hierarchyError = action.payload as string;
      });

    // Assign badge to item
    builder
      .addCase(assignBadgeToItem.pending, (state) => {
        state.hierarchyLoading = true;
        state.hierarchyError = null;
      })
      .addCase(assignBadgeToItem.fulfilled, (state) => {
        state.hierarchyLoading = false;
      })
      .addCase(assignBadgeToItem.rejected, (state, action) => {
        state.hierarchyLoading = false;
        state.hierarchyError = action.payload as string;
      });

    // Create hierarchy
    builder
      .addCase(createHierarchy.pending, (state) => {
        state.hierarchyLoading = true;
        state.hierarchyError = null;
      })
      .addCase(createHierarchy.fulfilled, (state) => {
        state.hierarchyLoading = false;
      })
      .addCase(createHierarchy.rejected, (state, action) => {
        state.hierarchyLoading = false;
        state.hierarchyError = action.payload as string;
      });
  },
});

// Actions
export const {
  setSelectedBadgeId,
  clearSelectedBadge,
  setFilters,
  resetFilters,
  clearErrors,
  clearBadges,
} = badgeSlice.actions;

// Selectors using entity adapter
export const {
  selectAll: selectAllBadges,
  selectById: selectBadgeById,
  selectIds: selectBadgeIds,
  selectEntities: selectBadgeEntities,
  selectTotal: selectBadgesCount,
} = badgesAdapter.getSelectors((state: RootState) => state.badges.badges);

// Custom selectors
export const selectFilteredBadges = (state: RootState) => {
  const badges = selectAllBadges(state);
  const filters = state.badges.filters;

  return badges.filter((badge) => {
    // Filter by search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      const matchesName = badge.name.toLowerCase().includes(query);
      const matchesDescription = badge.description
        .toLowerCase()
        .includes(query);
      if (!matchesName && !matchesDescription) {
        return false;
      }
    }

    // Filter by type
    if (filters.type !== undefined && badge.badgeType !== filters.type) {
      return false;
    }

    // Filter by active status
    if (filters.active !== undefined && badge.isActive !== filters.active) {
      return false;
    }

    // Filter by parent badge ID (for sub-badges)
    if (
      filters.parentBadgeId !== undefined &&
      badge.parentBadgeId !== filters.parentBadgeId
    ) {
      return false;
    }

    return true;
  });
};

export const selectActiveBadges = (state: RootState) => {
  return selectAllBadges(state).filter((badge) => badge.isActive);
};

export const selectParentBadges = (state: RootState) => {
  return selectAllBadges(state).filter(
    (badge) => badge.badgeType === "parent" && badge.isActive,
  );
};

export const selectSubBadges = (state: RootState) => {
  return selectAllBadges(state).filter(
    (badge) => badge.badgeType === "sub" && badge.isActive,
  );
};

export const selectSubBadgesByParent =
  (parentBadgeId: string) => (state: RootState) => {
    return selectAllBadges(state).filter(
      (badge) =>
        badge.badgeType === "sub" &&
        badge.parentBadgeId === parentBadgeId &&
        badge.isActive,
    );
  };

export const selectSelectedBadgeId = (state: RootState) =>
  state.badges.selectedBadgeId;

export const selectSelectedBadge = (state: RootState) => {
  const selectedId = state.badges.selectedBadgeId;
  if (!selectedId) return null;
  return selectBadgeById(state, selectedId);
};

export const selectBadgesLoading = (state: RootState) => state.badges.loading;
export const selectBadgesError = (state: RootState) => state.badges.error;
export const selectCreateLoading = (state: RootState) =>
  state.badges.createLoading;
export const selectCreateError = (state: RootState) => state.badges.createError;
export const selectUpdateLoading = (state: RootState) =>
  state.badges.updateLoading;
export const selectUpdateError = (state: RootState) => state.badges.updateError;
export const selectDeleteLoading = (state: RootState) =>
  state.badges.deleteLoading;
export const selectDeleteError = (state: RootState) => state.badges.deleteError;
export const selectHierarchyLoading = (state: RootState) =>
  state.badges.hierarchyLoading;
export const selectHierarchyError = (state: RootState) =>
  state.badges.hierarchyError;
export const selectBadgesFilters = (state: RootState) => state.badges.filters;
export const selectBadgesTotal = (state: RootState) => state.badges.total;

// Reducer
export default badgeSlice.reducer;
